import { useState, useRef, useEffect } from "react";
import { Check, Volume2, VolumeX, Play, Pause } from "lucide-react";

const VIDEO_1 = "/videos/warm.mp4";
const VIDEO_2 = "/videos/bw.mp4";

const STYLES = {
  celebration: {
    key: "celebration",
    overline: "In Color",
    title: "Cinematic Celebration",
    description:
      "Full of color and lifted into light — your loved one gently transformed among angels and doves, carried on warm cinematic motion. Joyful, radiant, and full of life.",
    tags: ["Full color", "Angel transformation", "Doves in flight", "Warm motion"],
    video: VIDEO_1,
    poster: "/videos/warm.jpg",
  },
  legacy: {
    key: "legacy",
    overline: "Black & White",
    title: "Angels Watching Over Us",
    description:
      "An intimate black-and-white family legacy — quiet, tender, and timeless. Soft light, gentle grain, and the comforting feeling of being watched over.",
    tags: ["Black & white", "Intimate", "Timeless", "Soft grain"],
    video: VIDEO_2,
    poster: "/videos/bw.jpg",
  },
};

export default function StyleShowcase() {
  const [active, setActive] = useState("celebration");
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const s = STYLES[active];

  // Reset playback when switching styles
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    v.play().catch(() => {});
  }, [active]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  return (
    <section id="work" data-testid="style-showcase" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-14">
          <div className="max-w-2xl">
            <div className="overline mb-4">Our Work</div>
            <h2 className="serif text-4xl sm:text-5xl lg:text-[56px] font-light leading-[1.05] text-[#F5F0E8]">
              Two styles,
              <span className="italic text-[#E6B89C]"> one promise.</span>
            </h2>
            <p className="mt-5 text-[#A0988E] spectral max-w-xl leading-relaxed">
              Every family is different, and every story deserves its own light.
              Both styles are hand-crafted with the same unhurried care.
            </p>
          </div>

          <div
            data-testid="style-switcher"
            className="inline-flex p-1 rounded-full border border-[rgba(197,165,90,0.25)] bg-[rgba(20,19,17,0.5)]"
          >
            {Object.values(STYLES).map((st) => (
              <button
                key={st.key}
                onClick={() => setActive(st.key)}
                data-testid={`style-switch-${st.key}`}
                className={`px-5 py-2.5 rounded-full text-sm transition-all ${
                  active === st.key
                    ? "bg-gradient-to-b from-[#E6C76A] to-[#C5A55A] text-[#1A1815] shadow-[0_8px_20px_-8px_rgba(212,175,55,0.5)]"
                    : "text-[#A0988E] hover:text-[#F5F0E8]"
                }`}
              >
                {st.key === "celebration" ? "Celebration" : "Legacy"}
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div data-testid="sample-video-frame" className="mx-auto w-full max-w-[calc(66vh*8/9)] rounded-[22px] overflow-hidden border border-[rgba(212,175,55,0.25)] bg-black">
            <div className="relative aspect-[8/9] group bg-black">
              <video
                ref={videoRef}
                key={s.video}
                data-testid={`sample-video-${s.key}`}
                src={s.video}
                poster={s.poster}
                autoPlay
                loop
                muted={muted}
                playsInline
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                className="absolute inset-0 w-full h-full object-contain"
                style={{ filter: "contrast(1.04)" }}
              />
              <div className="pointer-events-none absolute inset-x-0 top-0 h-20" style={{ background: "linear-gradient(180deg, rgba(11,10,9,0.55) 0%, rgba(11,10,9,0) 100%)" }} />

              {/* Controls */}
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <button
                  data-testid="toggle-video-mute"
                  data-video-sound=""
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent("tribute-video-sound", { detail: { sound: muted } }));
                    setMuted(!muted);
                  }}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(11,10,9,0.7)] border border-[rgba(212,175,55,0.3)] backdrop-blur hover:border-[rgba(212,175,55,0.6)] transition-colors"
                  aria-label="Toggle sound"
                >
                  {muted ? <VolumeX size={14} className="text-[#F5F0E8]" /> : <Volume2 size={14} className="text-[#D4AF37]" />}
                </button>
                <button
                  data-testid="toggle-video-play"
                  onClick={togglePlay}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(11,10,9,0.7)] border border-[rgba(212,175,55,0.3)] backdrop-blur hover:border-[rgba(212,175,55,0.6)] transition-colors"
                  aria-label="Play / pause"
                >
                  {playing ? <Pause size={14} className="text-[#F5F0E8]" /> : <Play size={14} className="text-[#D4AF37]" />}
                </button>
              </div>
            </div>

              <div data-testid="sample-video-caption" className="flex items-end justify-between gap-4 px-6 py-4 border-t border-[rgba(212,175,55,0.18)] bg-[#0E0D0B]">
                <div>
                  <div className="overline text-[#D4AF37]">{s.overline}</div>
                  <div className="serif italic text-[22px] sm:text-[26px] text-[#F5F0E8] mt-1">{s.title}</div>
                </div>
                <div className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(11,10,9,0.65)] border border-[rgba(212,175,55,0.3)]">
                  <span className="h-2 w-2 rounded-full bg-[#D4AF37] glow-pulse" />
                  <span className="text-[11px] tracking-widest text-[#F5F0E8]/80 uppercase">Sample Film</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="overline mb-3">{s.overline}</div>
            <h3 className="serif text-3xl sm:text-4xl font-light text-[#F5F0E8] leading-tight">{s.title}</h3>
            <p className="mt-5 text-[#A0988E] spectral leading-relaxed">{s.description}</p>

            <div className="mt-7 grid grid-cols-2 gap-3">
              {s.tags.map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-[#F5F0E8]/85">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)]">
                    <Check size={11} className="text-[#D4AF37]" strokeWidth={2.4} />
                  </span>
                  {t}
                </div>
              ))}
            </div>

            <div className="mt-8 p-5 card-surface">
              <div className="overline mb-2">Our Promise</div>
              <p className="text-[15px] text-[#F5F0E8]/90 leading-relaxed spectral italic">
                Every tribute is hand-finished frame by frame. No templates,
                no rushed slideshows — just your family's story, told the way
                it deserves to be remembered.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
